// @owners { team: patient-health }
import { COLORS } from '@alto/design-library-tokens';
import { Body, Card, InputRadio, InputRadioGroup, MdSpacing } from '@alto/design-system';
import React, { useEffect, useState } from 'react';
import { createPatientDiagnosis, deletePatientDiagnosisByCode } from '~shared/helpers/patientDiagnoses';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { useDispatchShared } from '~shared/store';
import { OnboardingButtonDesktop, OnboardingButtonMobile } from './OnboardingButton';
import OnboardingPageBackground from './OnboardingPageBackgroundContainer';
import { Header } from '~web/components/HeaderImage';
import { OnboardingContentWrapper } from '~web/components/Page';

export type Props = {
  readonly onContinue: () => void;
};

const OnboardingTargetedDiagnoses = ({ onContinue }: Props) => {
  const dispatch = useDispatchShared();
  const [hasType2Diabetes, setHasType2Diabetes] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    dispatch(
      sendAnalyticsEvent(
        createEvent(EVENTS.MEDICAL_CONDITIONS_PROMPTED, {
          condition: 'type 2 diabetes',
          origin: 'onboarding',
        }),
      ),
    );
  }, [dispatch]);

  const onSubmit = () => {
    setIsLoading(true);

    dispatch(
      sendAnalyticsEvent(
        createEvent(EVENTS.MEDICAL_CONDITIONS_SAVED, {
          added: hasType2Diabetes,
          condition: 'type 2 diabetes',
          origin: 'onboarding',
        }),
      ),
    );

    const promise = hasType2Diabetes
      ? createPatientDiagnosis({ params: { code: 'E11', notes: 'Type 2 diabetes mellitus' } })
      : deletePatientDiagnosisByCode({ code: 'E11' });

    // eslint-disable-next-line promise/catch-or-return
    promise.finally(() => {
      onContinue();
      setIsLoading(false);
    });
  };

  return (
    <OnboardingPageBackground>
      <OnboardingContentWrapper>
        <div>
          <Header title="Do you have type 2 diabetes?" />
          <MdSpacing />
          <Body
            color={COLORS.TEXT_COLORS.GREY}
            center
          >
            The manufacturer is updating their discount program for Mounjaro. Tell us your diabetes diagnosis below to
            qualify for the best price.
          </Body>
          <Card>
            <InputRadioGroup
              onValueChange={(value: string) => {
                if (value === 'yes') {
                  setHasType2Diabetes(true);
                } else if (value === 'no') {
                  setHasType2Diabetes(false);
                } else {
                  setHasType2Diabetes(null);
                }
              }}
              label="Do you have type 2 diabetes?"
            >
              <InputRadio
                key="yes"
                label="Yes"
                value="yes"
              />
              <InputRadio
                key="no"
                label="No"
                value="no"
              />
            </InputRadioGroup>
          </Card>
          <OnboardingButtonDesktop
            loading={isLoading}
            disabled={hasType2Diabetes === null}
            label="Continue"
            onClick={onSubmit}
          />
        </div>
      </OnboardingContentWrapper>
      <OnboardingButtonMobile
        loading={isLoading}
        disabled={hasType2Diabetes === null}
        label="Continue"
        onClick={onSubmit}
      />
    </OnboardingPageBackground>
  );
};

export default OnboardingTargetedDiagnoses;
